import React, { useMemo } from 'react';

import { AutoCompleteInput } from '@components/common/AutoCompleteInput';
import { WarningIcon } from '@components/common/AutoCompleteInput/WarningIcon';
import { buildOptionsWithTag } from '@components/common/AutoCompleteInput/buildOptionsWithTag';
import { EInputType } from '@components/mapping-panel/components/RuleBuilder/KeyValueBuilder/typed';
import {
  RuleBuilderContainer,
  RuleBuilderOperator,
  RuleBuilderTitle,
  Select,
} from '@components/mapping-panel/components/RuleBuilder/MetadataRuleItem/styled';
import { IMetadataRuleItem } from '@components/mapping-panel/components/RuleBuilder/MetadataRuleItem/typed';
import { EStringOp } from '@src/emp-3.0/types';
import { EQUAL_ONLY_METADATA } from '@components/mapping-panel/constants/constant';
import { isOnlyStars, validateString } from '@src/emp-3.0/utils';
import { ERenamedDimension } from '@src/common/constant/enum';
import { MATCH_PATTERN_ERROR_MESSAGE } from '@src/emp-3.0/constants/common';

const MetadataRuleItem = ({
  id,
  disabled,
  loading,
  active,
  isNoRules,
  dimensionName,
  data,
  options,
  onChange,
  onActive,
  operatorOptions,
}: IMetadataRuleItem) => {
  const title = dimensionName ? dimensionName : 'Metadata';
  const metadataRuleValue = data?.value?.[0];

  const metadataRuleOptions = buildOptionsWithTag(options ?? [], data?.key, metadataRuleValue);

  const { failed: isInvalidMetadataValue } = validateString(metadataRuleValue, {
    matchPattern: data?.op === EStringOp.MATCH_PATTERN,
  });
  const isEmptyValue = !metadataRuleValue || metadataRuleValue === '' || isNoRules;
  const isInvalidValue =
    data?.op === EStringOp.MATCH_PATTERN &&
    isInvalidMetadataValue &&
    isOnlyStars(metadataRuleValue);
  const autoCompleteInputStatus = isEmptyValue || isInvalidValue ? 'error' : undefined;

  const suffixText = isInvalidValue
    ? MATCH_PATTERN_ERROR_MESSAGE
    : 'The metadata all rule cannot be all null.';

  const suffix = useMemo(() => {
    if (autoCompleteInputStatus === 'error') {
      return <WarningIcon title={suffixText} iconStyle={{ color: '#ff4d4f' }} />;
    }
    return <span />;
  }, [autoCompleteInputStatus, suffixText]);

  let operators = operatorOptions;
  if (EQUAL_ONLY_METADATA.includes(data?.key || '')) {
    operators = operatorOptions?.filter(
      (op) => op.value === EStringOp.EQ || op.value === EStringOp.NOT_EQ,
    );
  }
  if (
    [ERenamedDimension.edited_screen_title, ERenamedDimension.edited_page_title].includes(data?.key)
  ) {
    operators = operatorOptions?.filter(
      (op) =>
        op.value === EStringOp.EQ ||
        op.value === EStringOp.NOT_EQ ||
        op.value === EStringOp.CONTAINS ||
        op.value === EStringOp.NOT_CONTAINS,
    );
  }
  return (
    <RuleBuilderContainer>
      <RuleBuilderTitle focus={active}>{title}</RuleBuilderTitle>
      <RuleBuilderOperator>
        <Select
          disabled={disabled}
          active={active}
          style={{ margin: '8px' }}
          options={operators}
          showArrow={false}
          defaultValue={operators?.[0].value}
          value={data?.op}
          onChange={(value) => {
            onChange?.(EInputType.METADATA_OPERATOR, value);
          }}
          onFocus={() => {
            onActive?.();
          }}
          dropdownStyle={{ minWidth: '350px' }}
          getPopupContainer={(triggerNode) => triggerNode.parentNode}
        />
      </RuleBuilderOperator>
      <AutoCompleteInput
        data-test-id={id}
        disabled={disabled}
        active={active}
        loading={loading}
        options={metadataRuleOptions}
        style={{ display: 'flex', flex: '1' }}
        value={metadataRuleValue}
        onChange={(value) => {
          onChange?.(EInputType.METADATA_VALUE, value);
        }}
        onFocus={() => {
          onActive?.();
        }}
        status={autoCompleteInputStatus}
        suffix={suffix}
      />
    </RuleBuilderContainer>
  );
};

export default MetadataRuleItem;
